import { Flex, Text, useMediaQuery } from "@chakra-ui/react";
import { useState } from "react";
import { Conversation } from "./types/Conversation.type";
import { ConversationList } from "./components/ConversationList";
import { ConversationView } from "./components/ConversationView";
import { CommunicationsPanelProps } from "./types/SharedProps.types";
import { useIsMenuCollapsed } from "newui/hooks/useMenuStatus";
import SavingOverlay from "newui/components/SavingOverlay";

/**
 * Container for communications, providing a llist of conversations and message view side-by-side
 * @param param0
 * @returns
 */
export const CommunicationsPanel: React.FC<CommunicationsPanelProps> = ({
	canCreateConversation: canCreate,
	additionalContextId,
	contextId,
	contextType,
	conversations,
	isLoading,
	onConversationCreated,
	onConversationSelected,
	noConversationsMessage,
	readonly = false,
	initialConversation,
}) => {
	const [selectedConversation, setSelectedConversation] =
		useState<Conversation | null>(initialConversation);
	const isMenuCollapsed = useIsMenuCollapsed();

	const [isScreenSmallWithMenu] = useMediaQuery("(max-width: 1180px)");
	const [isScreenSmallNoMenu] = useMediaQuery("(max-width: 820px)");
	const [isSendingMessage, setIsSendingMessage] = useState(false);

	// Determine if we should show conversation view on "mobile", or desktop if menu is not collapsed

	/**
	 * show conversationview if...
	 * conversation is selected AND
	 * (
	 *    screen is small AND menu is open
	 * )
	 */
	const showConversationView = !!selectedConversation;
	const isMobileView =
		(isScreenSmallWithMenu && !isMenuCollapsed) ||
		(isScreenSmallNoMenu && isMenuCollapsed);
	const hideConversationList = showConversationView && isMobileView;
	const listWidth = isMobileView ? "100%" : "360px";

	const goBackOnMobile = () => {
		setSelectedConversation(null);
	};

	if (!isLoading && conversations?.length === 0 && !canCreate) {
		return (
			<Flex
				w={{ base: "100%" }}
				className="bg-white pl-8"
				align={"flex-start"}
			>
				{noConversationsMessage ?? "There are no conversations to display."}
			</Flex>
		);
	}

	const conversationSelected = (conversation: Conversation) => {
		setSelectedConversation(conversation);
		onConversationSelected?.(conversation);
	};

	if (!selectedConversation && initialConversation) {
		setSelectedConversation(initialConversation);
	} else if (!selectedConversation && conversations.length === 1) {
		setSelectedConversation(conversations[0]);
	}

	return (
		<Flex
			h="full"
			w="full"
			direction={isMobileView ? "column" : "row"}
			className="overflow-hidden rounded-md border border-solid border-gray-200 bg-gray-50"
		>
			<SavingOverlay
				isShown={isSendingMessage}
				message="Sending Message..."
			/>
			{/* Conversation List */}
			<Flex
				// Mobile: hide when a conversation is selected
				display={hideConversationList ? "none" : "flex"}
				w={listWidth}
				flexShrink={isMobileView ? 1 : 0}
				className="border-r"
			>
				<ConversationList
					canCreateConversation={canCreate}
					additionalContextId={additionalContextId}
					contextId={contextId}
					contextType={contextType}
					conversations={conversations}
					isLoading={isLoading}
					selectedConversation={selectedConversation}
					onConversationCreated={onConversationCreated}
					onConversationSelected={conversationSelected}
				/>
			</Flex>

			{/* Conversation View */}
			<Flex
				flex="1"
				minW={0}
				display={hideConversationList || !isMobileView ? "flex" : "none"}
				className="bg-white"
			>
				{showConversationView ? (
					<ConversationView
						allowNewMessages={!readonly}
						conversation={selectedConversation}
						onSendingMessage={setIsSendingMessage}
						goBackOnMobile={goBackOnMobile}
						showBackButton={isMobileView}
						scrollOnLoad={!!initialConversation}
					/>
				) : (
					<Flex
						align="center"
						justify="center"
						h="full"
						w="full"
					>
						<Text color="gray.400">
							Select a conversation on the left to view messages
							{canCreate && " or create a new one."}
						</Text>
					</Flex>
				)}
			</Flex>
		</Flex>
	);
};
